import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, apiConfig } from '../config/api';

interface JourneyAnalyticsData {
  stage: string;
  count: number;
  percentage: number;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  journeyStage: string;
}

const JourneyAnalytics: React.FC = () => {
  const [journeyData, setJourneyData] = useState<JourneyAnalyticsData[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  const { user: _user } = useAuth(); // Prefix with underscore to indicate intentionally unused
  const navigate = useNavigate();

  const fetchJourneyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl(apiConfig.endpoints.analytics.summary));
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to fetch journey data');
      }

      const summary = data.data.summary;
      const total = summary.totalMembers;
      
      const journeyAnalytics: JourneyAnalyticsData[] = [
        { stage: 'Leader', count: summary.leaders, percentage: total > 0 ? (summary.leaders / total) * 100 : 0 },
        { stage: 'Engaged Member', count: summary.engagedMembers, percentage: total > 0 ? (summary.engagedMembers / total) * 100 : 0 },
        { stage: 'Member', count: summary.members, percentage: total > 0 ? (summary.members / total) * 100 : 0 },
        { stage: 'Attendee', count: summary.attendees, percentage: total > 0 ? (summary.attendees / total) * 100 : 0 },
        { stage: 'Visitor', count: summary.visitors, percentage: total > 0 ? (summary.visitors / total) * 100 : 0 }
      ];

      setJourneyData(journeyAnalytics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(buildApiUrl(apiConfig.endpoints.analytics.memberJourney) + '?limit=50');
      const data = await response.json();

      if (data.success) {
        setMembers(data.data);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  useEffect(() => {
    // Fetch data when component mounts
    fetchJourneyData();
    fetchMembers();
  }, []); // Only run once on mount

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Engaged Member':
        return 'bg-emerald-500';
      case 'Member':
        return 'bg-blue-500';
      case 'Attendee':
        return 'bg-yellow-500';
      case 'New Visitor':
        return 'bg-orange-500';
      case 'Visitor':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getJourneyStageColor = (stage: string) => {
    switch (stage) {
      case 'Leader': return 'bg-purple-100 text-purple-800';
      case 'Partner': return 'bg-blue-100 text-blue-800';
      case 'Engaged Member': return 'bg-green-100 text-green-800';
      case 'Member': return 'bg-yellow-100 text-yellow-800';
      case 'Attendee': return 'bg-blue-100 text-blue-800';
      case 'New Visitor': return 'bg-orange-100 text-orange-800';
      case 'Visitor': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleMemberClick = (memberId: string) => {
    navigate(`/member/${memberId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading journey analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Analytics</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchJourneyData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'members', name: 'Member List', icon: '👥' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">Member Journey Analytics</h1>
            <p className="text-gray-600 mt-1">Detailed analysis of member engagement progression</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Journey Stage Breakdown */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Journey Stage Distribution</h2>
                  <p className="text-sm text-gray-600 mb-4">Percentage breakdown of members by journey stage</p>
                  <div className="space-y-4">
                    {journeyData.map((item) => (
                      <div key={item.stage} className="flex items-center">
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-900">{item.stage}</span>
                            <span className="text-sm text-gray-500">{item.count} members ({Math.round(item.percentage)}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getStageColor(item.stage)}`}
                              style={{ width: `${item.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Journey Insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Journey Insights</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                        <span className="text-sm font-medium text-emerald-800">Engaged Members</span>
                        <span className="text-sm font-bold text-emerald-900">{journeyData.find(d => d.stage === 'Engaged Member')?.count || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <span className="text-sm font-medium text-blue-800">Active Members</span>
                        <span className="text-sm font-bold text-blue-900">{journeyData.find(d => d.stage === 'Member')?.count || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <span className="text-sm font-medium text-yellow-800">Regular Attendees</span>
                        <span className="text-sm font-bold text-yellow-900">{journeyData.find(d => d.stage === 'Attendee')?.count || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <span className="text-sm font-medium text-orange-800">First Time Visitors</span>
                        <span className="text-sm font-bold text-orange-900">{journeyData.find(d => d.stage === 'New Visitor')?.count || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-800">Prospects</span>
                        <span className="text-sm font-bold text-gray-900">{journeyData.find(d => d.stage === 'Visitor')?.count || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Metrics</h3>
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-emerald-600">
                          {Math.round(journeyData.find(d => d.stage === 'Engaged Member')?.percentage || 0)}%
                        </div>
                        <p className="text-sm text-gray-600">Engagement Rate</p>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600">
                          {Math.round((journeyData.find(d => d.stage === 'Engaged Member')?.count || 0) + (journeyData.find(d => d.stage === 'Member')?.count || 0))}
                        </div>
                        <p className="text-sm text-gray-600">Active Members</p>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-yellow-600">
                          {Math.round(journeyData.find(d => d.stage === 'Visitor')?.percentage || 0)}%
                        </div>
                        <p className="text-sm text-gray-600">Visitor Rate</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Member List</h3>
                  <p className="text-sm text-gray-500">Click on any member to view detailed analytics</p>
                </div>
                
                {members.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No members found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Journey Stage
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {members.map((member) => (
                          <tr key={member.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {member.firstName} {member.lastName}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{member.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getJourneyStageColor(member.journeyStage)}`}>
                                {member.journeyStage}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleMemberClick(member.id)}
                                className="text-blue-600 hover:text-blue-900 font-medium"
                              >
                                View Details →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyAnalytics; 